import _ from 'lodash';
import moment from 'moment';

import {
  getEnumValue,
  parseOptionalNumericEnumFromApi,
} from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';

const { AICreditGrantType, Currency } = CoreEnums;

const UUID_FALLBACK = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

export const generateRechargeIdempotencyKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : UUID_FALLBACK();

export const resolveRechargeIdempotencyKey = (existingKey) => {
  if (existingKey) return existingKey;
  return generateRechargeIdempotencyKey();
};

/** AceInput/FormBuilder select uses labelInValue → `{ value, label }`; API expects primitives. */
export const unwrapFormSelectValue = (raw) => {
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw.value ?? raw.id ?? raw;
  }
  return raw;
};

const toApiEnumMember = (enumObject, raw) => {
  const resolved = parseOptionalNumericEnumFromApi(enumObject, unwrapFormSelectValue(raw));
  if (resolved === undefined) return null;
  const enumKey = getEnumValue(enumObject, resolved);
  return enumKey ? _.kebabCase(enumKey) : null;
};

const toApiCurrencyCode = (raw) => {
  const resolved = parseOptionalNumericEnumFromApi(Currency, unwrapFormSelectValue(raw));
  if (resolved === undefined) return null;
  return getEnumValue(Currency, resolved);
};

const toOptionalApiDateTime = (value) => {
  if (value == null || value === '') return undefined;

  const date = moment.isMoment(value) ? value : moment(value);
  if (!date.isValid()) return undefined;

  return date.toISOString();
};

export const buildRechargePayload = (formData, existingKey) => {
  const validFrom = toOptionalApiDateTime(formData?.validFrom);
  const expiresAt = toOptionalApiDateTime(formData?.expiresAt);

  return {
    type: toApiEnumMember(AICreditGrantType, formData?.type),
    amount: Number(formData?.amount),
    currency: toApiCurrencyCode(formData?.currency),
    reason: formData?.reason,
    internalComment: formData?.internalComment,
    isCustomerVisible: formData?.isCustomerVisible,
    associateToCurrentPeriod: formData?.associateToCurrentPeriod,
    ...(validFrom != null && { validFrom }),
    ...(expiresAt != null && { expiresAt }),
    idempotencyKey: resolveRechargeIdempotencyKey(existingKey),
  };
};
